import type { AgentOrchestratorStepResult } from '@axilon-apps/domains-ontology-ui-state'

export type StepStatus = 'pending' | 'running' | 'done'

/**
 * Lifecycle status of a single plan step, derived from its OWN result rather
 * than its position in the list. A step is `running` once it has started (a
 * `step_start` / `step_text_delta` upserts a partial result with
 * `completed: false`) and stays so until it completes or the whole turn
 * finalizes. Because this is per-step, a parallel dispatch wave can have
 * several `running` steps at once — unlike the old positional rule where only
 * the last step with content counted as active.
 *
 * Once the turn is complete, any *started* step settles to `done` — during
 * the live finalize window `step_complete` can lag the finalized summary by
 * a beat, and flashing the started step back to the hollow `pending` circle
 * (● → ○ → ✓) reads as a glitch. Steps that never started stay `pending`.
 */
export function deriveStepStatus(
  stepResults: Record<number, AgentOrchestratorStepResult>,
  stepIndex: number,
  isComplete: boolean
): StepStatus {
  const result = stepResults[stepIndex]
  if (result === undefined) return 'pending'
  if (result.completed) return 'done'
  return isComplete ? 'done' : 'running'
}
